import {
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMonths,
  subQuarters,
  subYears,
} from "date-fns";
import { de } from "date-fns/locale";
import { noop } from "lodash";
import { CalendarIcon, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { ActiveModifiers, DateRange } from "react-day-picker";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import i18n from "@/localisation/i18next";

type Props = {
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  value?: {
    from: Date;
    to: Date;
  };
  initialFocus?: boolean;
  placeholder: string;
  disabled?: boolean;
  Icon?: LucideIcon;
  onChange?: (range: DateRange | null) => void;
  highlightWhenSelected?: boolean;
  onFocus?: (
    day: Date,
    activeModifiers: ActiveModifiers,
    e: React.FocusEvent<Element, Element> | React.KeyboardEvent<Element>,
  ) => void;
  onBlur?: (
    day: Date,
    activeModifiers: ActiveModifiers,
    e: React.FocusEvent<Element, Element> | React.KeyboardEvent<Element>,
  ) => void;
};

export function DatePickerWithRange({
  className,
  value,
  placeholder,
  initialFocus = true,
  disabled = false,
  Icon = CalendarIcon,
  onChange = noop,
  highlightWhenSelected = false,
  onFocus = noop,
  onBlur = noop,
}: Props) {
  const [date, setDate] = useState<DateRange | undefined>(value);

  useEffect(() => {
    setDate(value);
  }, [value]);

  const handleOnChange = (range: DateRange | undefined) => {
    if (!range) return;

    setDate(range);
    onChange(range);
  };

  const handleOnFocus = (
    day: Date,
    activeModifiers: ActiveModifiers,
    e: React.FocusEvent<Element, Element> | React.KeyboardEvent<Element>,
  ) => {
    onFocus(day, activeModifiers, e);
  };

  const handleOnBlur = (
    day: Date,
    activeModifiers: ActiveModifiers,
    e: React.FocusEvent<Element, Element> | React.KeyboardEvent<Element>,
  ) => {
    onBlur(day, activeModifiers, e);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDate(undefined);
    onChange(null);
  };

  const handleQuickSelect = (type: string) => {
    const now = new Date();
    let range: { from: Date; to: Date };

    switch (type) {
      case "current-month":
        range = {
          from: startOfMonth(now),
          to: endOfMonth(now),
        };
        break;
      case "past-month": {
        const lastMonth = subMonths(now, 1);
        range = {
          from: startOfMonth(lastMonth),
          to: endOfMonth(lastMonth),
        };
        break;
      }
      case "current-quarter":
        range = {
          from: startOfQuarter(now),
          to: endOfQuarter(now),
        };
        break;
      case "past-quarter": {
        const lastQuarter = subQuarters(now, 1);
        range = {
          from: startOfQuarter(lastQuarter),
          to: endOfQuarter(lastQuarter),
        };
        break;
      }
      case "current-year":
        range = {
          from: startOfYear(now),
          to: endOfYear(now),
        };
        break;
      case "past-year": {
        const lastYear = subYears(now, 1);
        range = {
          from: startOfYear(lastYear),
          to: endOfYear(lastYear),
        };
        break;
      }
      default:
        return;
    }

    setDate(range);
    onChange(range);
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-[210px] justify-start text-left font-normal gap-1",
              !date && "text-muted-foreground",
              date &&
                (date.from || date.to) &&
                highlightWhenSelected &&
                "bg-secondary",
            )}
            disabled={disabled}
          >
            <Icon className="w-4 h-4" />
            {date?.from ? (
              <>
                <span className="flex-1">
                  {date.to ? (
                    <>
                      {format(date.from, "dd.MM.yy", { locale: de })} -{" "}
                      {format(date.to, "dd.MM.yy", { locale: de })}
                    </>
                  ) : (
                    format(date.from, "dd.MM.yy", { locale: de })
                  )}
                </span>
                <X
                  className="w-4 h-4 opacity-50 hover:opacity-100 cursor-pointer"
                  onClick={handleClear}
                />
              </>
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="flex flex-col">
            {/* Quick Select Buttons */}
            <div className="p-3 border-b">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                Schnellauswahl
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickSelect("current-month")}
                  className="text-xs h-8"
                >
                  Aktueller Monat
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickSelect("past-month")}
                  className="text-xs h-8"
                >
                  Letzter Monat
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickSelect("current-quarter")}
                  className="text-xs h-8"
                >
                  Aktuelles Quartal
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickSelect("past-quarter")}
                  className="text-xs h-8"
                >
                  Letztes Quartal
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickSelect("current-year")}
                  className="text-xs h-8"
                >
                  Aktuelles Jahr
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickSelect("past-year")}
                  className="text-xs h-8"
                >
                  Letztes Jahr
                </Button>
              </div>
            </div>
            {/* Calendar */}
            <Calendar
              initialFocus={initialFocus}
              mode="range"
              defaultMonth={date?.from}
              selected={date}
              locale={de}
              onSelect={handleOnChange}
              onDayFocus={(day, activeModifiers, e) =>
                handleOnFocus(day, activeModifiers, e)
              }
              onDayBlur={(day, activeModifiers, e) =>
                handleOnBlur(day, activeModifiers, e)
              }
              numberOfMonths={2}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
